/**
 * Section-panel builders — one panel per math section.
 *
 * REN-12: the results view carries one plain-English panel for each of the
 * four math sections the engine composes — mortgage cost, opportunity cost,
 * transaction cost, and the break-even conclusion. Each panel is a
 * headline number, a one-sentence claim that names the numbers driving
 * it, and a "show the math" progressive disclosure (a WhyNumberSpec) so a
 * spreadsheet-minded reader can reconstruct the panel from its inputs.
 *
 * The one-sentence bar is the whole point: if we can't defend a number in
 * one sentence, we don't ship the panel. That is why these builders live
 * in a single module — the sentences and popover copy sit next to each
 * other so drift is impossible.
 *
 * Everything here is pure. DOM lives in ./panels rendering inside
 * results.ts; wording, formulas, and input lists live here so they can be
 * pinned by unit tests without a browser.
 */

import type { ScenarioInputs } from "../defaults";
import type { BreakEvenResult } from "../math/breakEven";
import type { Jurisdiction } from "../math/jurisdictions";
import { amortizeMortgage } from "../math/mortgage";
import { buyCost, sellCost } from "../math/transactions";
import { formatOrdinalYear } from "./format";
import type { WhyNumberSpec } from "./whyNumber";

const MONTHS_PER_YEAR = 12;
const PERCENT_DIVISOR = 100;

export type CurrencyFmt = (value: number) => string;

/** One section panel — one heading, one headline number, one sentence. */
export interface PanelSpec {
  readonly key: "mortgage" | "opportunity" | "transactions" | "breakEven";
  readonly heading: string;
  /** Big number rendered next to the heading. Empty string suppresses. */
  readonly headline: string;
  /** Single defensible sentence that embeds the numbers a reader needs. */
  readonly sentence: string;
  /** Progressive-disclosure detail — formula, inputs, computed answer. */
  readonly why: WhyNumberSpec;
}

/**
 * Build all four section panels for the current scenario in one pass so a
 * caller can update every panel from one recompute without re-running the
 * engine per panel.
 */
export function buildPanels(
  state: ScenarioInputs,
  result: BreakEvenResult,
  jurisdiction: Jurisdiction | undefined,
  currencyFmt: CurrencyFmt,
): readonly PanelSpec[] {
  return [
    buildMortgagePanel(state, currencyFmt),
    buildOpportunityPanel(state, jurisdiction, currencyFmt),
    buildTransactionsPanel(state, jurisdiction, currencyFmt),
    buildBreakEvenPanel(state, result, currencyFmt),
  ];
}

/**
 * Mortgage cost — monthly principal-and-interest payment, plus the interest
 * paid and loan balance at the horizon. These three numbers together tell a
 * reader what fraction of their mortgage is "gone" versus "still owed" by
 * the time they sell.
 */
export function buildMortgagePanel(
  state: ScenarioInputs,
  currencyFmt: CurrencyFmt,
): PanelSpec {
  const downPayment = state.homePrice * (state.downPaymentPercent / PERCENT_DIVISOR);
  const schedule = amortizeMortgage({
    homePrice: state.homePrice,
    downPayment,
    annualInterestRatePercent: state.annualInterestRatePercent,
    termYears: state.termYears,
  });
  const horizonMonth = Math.min(
    state.horizonYears * MONTHS_PER_YEAR,
    schedule.months,
  );
  const rows = schedule.schedule.slice(0, horizonMonth);
  const interestToHorizon = rows.reduce((sum, r) => sum + r.interest, 0);
  const balanceAtHorizon = rows.length > 0
    ? rows[rows.length - 1]!.balance
    : schedule.loanAmount;
  const loanFullyPaid = state.horizonYears * MONTHS_PER_YEAR >= schedule.months;

  const balanceSentence = loanFullyPaid
    ? "the loan is fully paid off by the end of your horizon"
    : `your remaining loan balance is ${currencyFmt(balanceAtHorizon)}`;

  const sentence = schedule.monthlyPayment === 0
    ? `You're paying cash for the home, so there's no mortgage payment.`
    : `Your ${currencyFmt(schedule.loanAmount)} mortgage costs ${currencyFmt(schedule.monthlyPayment)}/mo in principal and interest; over ${state.horizonYears} year${state.horizonYears === 1 ? "" : "s"} you'd pay ${currencyFmt(interestToHorizon)} in interest and ${balanceSentence}.`;

  return {
    key: "mortgage",
    heading: "Mortgage cost",
    headline: `${currencyFmt(schedule.monthlyPayment)}/mo`,
    sentence,
    why: {
      formulaTitle: "How the mortgage payment is computed",
      formulaLines: [
        "Loan principal P = home price − down payment.",
        "Monthly payment M = P × i(1+i)ⁿ / ((1+i)ⁿ − 1), where i is the monthly rate and n = term × 12.",
        "Each month, interest = balance × monthly rate; principal = M − interest; balance falls by that principal.",
        "Interest to your horizon = sum of the interest column across the first horizon × 12 months.",
      ],
      inputs: [
        { label: "Home price", value: currencyFmt(state.homePrice) },
        {
          label: "Down payment",
          value: `${trimZeros(state.downPaymentPercent)}% (${currencyFmt(downPayment)})`,
        },
        { label: "Loan principal", value: currencyFmt(schedule.loanAmount) },
        {
          label: "Mortgage rate",
          value: `${trimZeros(state.annualInterestRatePercent)}% per year`,
        },
        { label: "Mortgage term", value: `${state.termYears} years` },
        { label: "Horizon", value: `${state.horizonYears} years` },
      ],
      result: {
        label: schedule.monthlyPayment === 0
          ? "No mortgage"
          : `Payment, interest to year ${state.horizonYears}, balance at year ${state.horizonYears}`,
        value: schedule.monthlyPayment === 0
          ? "Cash purchase — nothing owed."
          : loanFullyPaid
            ? `${currencyFmt(schedule.monthlyPayment)}/mo · ${currencyFmt(interestToHorizon)} interest · loan paid off`
            : `${currencyFmt(schedule.monthlyPayment)}/mo · ${currencyFmt(interestToHorizon)} interest · ${currencyFmt(balanceAtHorizon)} still owed`,
      },
    },
  };
}

/**
 * Opportunity cost — the head-start renting keeps by not sinking the down
 * payment (plus closing costs) into a house. Modelled as a lump-sum FV at
 * the investment return rate over the horizon, which matches what the
 * engine does to seed the rent-path portfolio.
 */
export function buildOpportunityPanel(
  state: ScenarioInputs,
  jurisdiction: Jurisdiction | undefined,
  currencyFmt: CurrencyFmt,
): PanelSpec {
  const downPayment = state.homePrice * (state.downPaymentPercent / PERCENT_DIVISOR);
  const closing = jurisdiction ? buyCost(state.homePrice, jurisdiction) : null;
  const closingTotal = closing?.total ?? 0;
  const initialCapital = downPayment + closingTotal;

  const monthlyDiscountRate = state.annualDiscountRatePercent / PERCENT_DIVISOR / MONTHS_PER_YEAR;
  const horizonMonths = state.horizonYears * MONTHS_PER_YEAR;
  const futureValue = initialCapital * Math.pow(1 + monthlyDiscountRate, horizonMonths);
  const growth = futureValue - initialCapital;

  const closingClause = closing
    ? ` (${currencyFmt(downPayment)} down + ${currencyFmt(closingTotal)} closing costs)`
    : "";
  const sentence = initialCapital <= 0
    ? "With no down payment and no closing costs, there's no head-start to invest."
    : `A renter who invested the ${currencyFmt(initialCapital)} you'd sink into buying${closingClause} at ${trimZeros(state.annualDiscountRatePercent)}%/yr would have about ${currencyFmt(futureValue)} after ${state.horizonYears} year${state.horizonYears === 1 ? "" : "s"} — a ${currencyFmt(growth)} head-start the owner has to overtake.`;

  return {
    key: "opportunity",
    heading: "Opportunity cost",
    headline: currencyFmt(futureValue),
    sentence,
    why: {
      formulaTitle: "How the renter's head-start compounds",
      formulaLines: [
        "Initial capital = down payment + one-off closing costs.",
        "Future value = initial capital × (1 + r/12)^(horizon × 12), where r is the annual investment return.",
        "This is the same seed the engine uses to start the renter's investment portfolio, so the head-start compounds symmetrically with the owner's monthly savings.",
      ],
      inputs: [
        { label: "Home price", value: currencyFmt(state.homePrice) },
        {
          label: "Down payment",
          value: `${trimZeros(state.downPaymentPercent)}% (${currencyFmt(downPayment)})`,
        },
        {
          label: "Closing costs (one-off)",
          value: closing ? currencyFmt(closingTotal) : "unknown jurisdiction",
        },
        { label: "Initial capital", value: currencyFmt(initialCapital) },
        {
          label: "Investment return",
          value: `${trimZeros(state.annualDiscountRatePercent)}% per year`,
        },
        { label: "Horizon", value: `${state.horizonYears} years` },
      ],
      result: {
        label: `Renter's invested head-start at year ${state.horizonYears}`,
        value: `${currencyFmt(initialCapital)} → ${currencyFmt(futureValue)} (+${currencyFmt(growth)})`,
      },
    },
  };
}

/**
 * Transaction cost — the round-trip friction of buying today and hypothetically
 * selling at the horizon. Uses the appreciated home value for the sell side so
 * a reader sees the commission scale with what the house is actually worth
 * when they sell, not what they paid.
 */
export function buildTransactionsPanel(
  state: ScenarioInputs,
  jurisdiction: Jurisdiction | undefined,
  currencyFmt: CurrencyFmt,
): PanelSpec {
  if (!jurisdiction) {
    return {
      key: "transactions",
      heading: "Transaction cost",
      headline: "—",
      sentence: "Select a jurisdiction to see closing and selling costs.",
      why: {
        formulaTitle: "How transaction costs are computed",
        formulaLines: [
          "Buy cost = purchase price × closing-cost rate + fixed fees + transfer tax on purchase.",
          "Sell cost = home value at sale × selling-agent commission.",
          "Home value at sale = purchase price × (1 + appreciation)^horizon.",
        ],
        inputs: [{ label: "Jurisdiction", value: state.jurisdictionId }],
        result: { label: "Round-trip friction", value: "—" },
      },
    };
  }

  const buy = buyCost(state.homePrice, jurisdiction);
  const horizonHomeValue = state.homePrice * Math.pow(
    1 + state.annualHomeAppreciationPercent / PERCENT_DIVISOR,
    state.horizonYears,
  );
  const sell = sellCost(horizonHomeValue, jurisdiction);
  const roundTrip = buy.total + sell.total;
  const commissionPercent = jurisdiction.sellerAgentCommission.percentOfValue * PERCENT_DIVISOR;

  const sentence = `Buying costs about ${currencyFmt(buy.total)} up front in ${jurisdiction.name}, and selling at your projected year-${state.horizonYears} price of ${currencyFmt(horizonHomeValue)} would take another ${currencyFmt(sell.total)} in agent commission — around ${currencyFmt(roundTrip)} of round-trip friction owning has to earn back.`;

  return {
    key: "transactions",
    heading: "Transaction cost",
    headline: currencyFmt(roundTrip),
    sentence,
    why: {
      formulaTitle: "How transaction costs are computed",
      formulaLines: [
        "Buy cost = purchase price × closing-cost rate + fixed closing fees + transfer / stamp duty.",
        "Sell cost = home value at sale × selling-agent commission.",
        "Home value at sale = purchase price × (1 + appreciation)^horizon.",
      ],
      inputs: [
        { label: "Home price", value: currencyFmt(state.homePrice) },
        {
          label: "Buyer closing costs",
          value: currencyFmt(buy.closingCosts),
        },
        {
          label: "Transfer / stamp duty",
          value: currencyFmt(buy.transferTax),
        },
        {
          label: "Home appreciation",
          value: `${trimZeros(state.annualHomeAppreciationPercent)}% per year`,
        },
        {
          label: `Home value at year ${state.horizonYears}`,
          value: currencyFmt(horizonHomeValue),
        },
        {
          label: "Selling commission",
          value: `${trimZeros(commissionPercent)}% of sale price`,
        },
        { label: "Jurisdiction", value: jurisdiction.name },
      ],
      result: {
        label: "Round-trip friction",
        value: `${currencyFmt(buy.total)} to buy + ${currencyFmt(sell.total)} to sell = ${currencyFmt(roundTrip)}`,
      },
    },
  };
}

/**
 * Break-even — the section-panel restatement of the headline answer. Names
 * the year (or "never"), the size of the winning margin, and points a reader
 * at the underlying comparison so they know what number is being compared to
 * what.
 */
export function buildBreakEvenPanel(
  state: ScenarioInputs,
  result: BreakEvenResult,
  currencyFmt: CurrencyFmt,
): PanelSpec {
  const horizonMonth = state.horizonYears * MONTHS_PER_YEAR;
  const horizonPoint = result.cumulativeNetWorth[horizonMonth - 1];
  const ownAtHorizon = horizonPoint?.ownPath ?? 0;
  const rentAtHorizon = horizonPoint?.rentPath ?? 0;
  const gapAtHorizon = ownAtHorizon - rentAtHorizon;

  let headline: string;
  let sentence: string;
  let resultCopy: { label: string; value: string };

  if (result.breakEvenYear === null) {
    headline = "Renting wins";
    sentence = `Owning never overtakes renting inside your ${state.horizonYears}-year horizon — by year ${state.horizonYears}, renting is ahead by about ${currencyFmt(Math.max(0, rentAtHorizon - ownAtHorizon))}.`;
    resultCopy = {
      label: "Break-even year",
      value: `Never within ${state.horizonYears} years — rent ${currencyFmt(rentAtHorizon)} vs. own ${currencyFmt(ownAtHorizon)}.`,
    };
  } else {
    const year = result.breakEvenYear;
    const beIdx = year * MONTHS_PER_YEAR - 1;
    const bePoint = result.cumulativeNetWorth[beIdx];
    const marginAtBreakEven = bePoint ? Math.max(0, bePoint.ownPath - bePoint.rentPath) : 0;
    headline = `Year ${year}`;
    const horizonClause = gapAtHorizon > 0
      ? ` and by year ${state.horizonYears} owning is ahead by ${currencyFmt(gapAtHorizon)}`
      : "";
    sentence = `Owning first matches renting in the ${formatOrdinalYear(year)} year — the buyer's home equity plus banked cashflow ties the renter's invested head-start${horizonClause}.`;
    resultCopy = {
      label: "Break-even year",
      value: marginAtBreakEven > 0
        ? `Year ${year} — owning is ${currencyFmt(marginAtBreakEven)} ahead at the ${formatOrdinalYear(year)}-year mark.`
        : `Year ${year} — owning first ties renting at the ${formatOrdinalYear(year)}-year mark.`,
    };
  }

  return {
    key: "breakEven",
    heading: "Break-even",
    headline,
    sentence,
    why: {
      formulaTitle: "How the break-even year is chosen",
      formulaLines: [
        state.neverSell
          ? "Own net worth(Y) = home value − loan balance + owner's investment portfolio. (Selling costs are excluded — you toggled indefinite hold.)"
          : "Own net worth(Y) = home value − loan balance − hypothetical selling costs + owner's investment portfolio.",
        "Rent net worth(Y) = renter's investment portfolio (seeded with the down payment + closing costs).",
        "Each month the cheaper path banks the housing-cost difference at your investment return, so neither side gets a free windfall.",
        "Break-even year Y = the smallest whole year at which own net worth ≥ rent net worth.",
      ],
      inputs: [
        { label: "Monthly rent", value: `${currencyFmt(state.monthlyRent)}/mo` },
        { label: "Home price", value: currencyFmt(state.homePrice) },
        {
          label: "Down payment",
          value: `${trimZeros(state.downPaymentPercent)}%`,
        },
        {
          label: "Mortgage rate",
          value: `${trimZeros(state.annualInterestRatePercent)}% per year`,
        },
        { label: "Horizon", value: `${state.horizonYears} years` },
        {
          label: "Home appreciation",
          value: `${trimZeros(state.annualHomeAppreciationPercent)}% per year`,
        },
        {
          label: "Investment return",
          value: `${trimZeros(state.annualDiscountRatePercent)}% per year`,
        },
        {
          label: "Rent inflation",
          value: `${trimZeros(state.annualRentInflationPercent)}% per year`,
        },
      ],
      result: resultCopy,
    },
  };
}

function trimZeros(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 100) / 100);
}
